<template>
  <component
    :is="pdpUrl ? UiSmartLink : 'div'"
    :to="pdpUrl"
    class="horizontal-product-card"
  >
    <UiSmartImage
      :src="imageUrl"
      :alt="productName"
      aspect-ratio="1/1"
      class="product-card-image"
    />

    <div class="product-card-details">
      <div class="product-card-brand">{{ brandName }}</div>

      <div class="product-card-name">{{ productName }}</div>

      <div
        v-if="variantName.toLowerCase() !== 'default'"
        class="product-card-variant-name"
      >
        {{ variantName }}
      </div>

      <UiPrice
        v-if="props.price"
        :original-price="props.price?.originalPrice"
        :sales-price="props.price?.salesPrice"
        :sales-text="props.price?.salesText"
        :open-text="props.price?.openText"
      />
      <UiRating
        v-if="rating"
        type="default"
        :rating-count="rating"
        :review-text="reviews"
      />
      <div v-if="highlightText" class="product-card-highlight">
        {{ highlightText }}
      </div>
    </div>
  </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import UiRating from '@sephora-asia/ui-rating/UiRating.vue'
import UiPrice from '@sephora-asia/ui-price/UiPrice.vue'
import UiSmartLink from '@sephora-asia/ui-smart-link'
import UiSmartImage from '@sephora-asia/ui-smart-image'
import type { HorizontalProductCardProps } from './types'

const props = withDefaults(defineProps<HorizontalProductCardProps>(), {
  highlightText: '',
  rating: 0,
  reviewCount: 0,
  variantName: '',
})

const reviews = computed((): string => {
  return Number(props.reviewCount) > 0 ? `(${props.reviewCount})` : ''
})
</script>

<style lang="scss" scoped>
.horizontal-product-card {
  display: flex;
  gap: 16px;
  text-decoration: none;
  color: var(--color-base-black);
}
.product-card-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.product-card-image {
  width: 80px;
  height: 80px;
  flex-shrink: 0;
}
.product-card-brand,
.product-card-price {
  @extend .headline-m;
}
.product-card-name,
.product-card-brand,
.product-card-variant-name {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
}

.product-card-brand,
.product-card-variant-name {
  -webkit-line-clamp: 1;
}

.product-card-name {
  @extend .body-m;
  -webkit-line-clamp: 2;
}
.product-card-variant-name {
  @extend .body-s;
  color: var(--color-base-grey-50);
}
.product-card-highlight {
  @extend .body-m;
  color: var(--color-base-red-100);
}
</style>
